import { getAuthToken } from '@/dataProvider/axios';
import { ragServiceAxios, ragServiceJobsAxios, RAG_SERVICE_BASE_URL, RAG_SERVICE_JOBS_BASE_URL } from './client';
import type {
  ApiMetadataItem,
  ApiFindingsObject,
  SecurityFiltersResponse,
  SecurityFindingsResponse,
  SecurityFindingsSummary,
  SecurityFindingsPagination,
  SecurityFinding,
  SecurityHealthResponse,
} from './types';

export async function getSecurityHealth(params: {
  org_id: number | string;
  user_id: number | string;
}): Promise<SecurityHealthResponse> {
  const response = await ragServiceAxios.get<SecurityHealthResponse>('/security/health', {
    params: {
      org_id: params.org_id,
      user_id: params.user_id,
    },
    headers: {
      'X-Organization-ID': String(params.org_id),
      'X-User-ID': String(params.user_id),
    },
  });

  // Response format after interceptor: { org_id, user_id, security_health }
  if (response.data && 'security_health' in response.data) {
    return response.data;
  }

  // Throw error if response format is unexpected
  throw new Error('Invalid response format from /security/health endpoint');
}

/**
 * Get security filters
 * GET /security/filters?org_id=xxx&user_id=xxx
 *
 * @param params Query parameters
 * @returns Security filters data
 */
export async function getSecurityFilters(params: {
  org_id: number | string;
  user_id: number | string;
}): Promise<SecurityFiltersResponse> {
  const response = await ragServiceAxios.get<SecurityFiltersResponse>('/security/filters', {
    params: {
      org_id: params.org_id,
      user_id: params.user_id,
    },
    headers: {
      'X-Organization-ID': String(params.org_id),
      'X-User-ID': String(params.user_id),
    },
  });

  // Response format after interceptor: { severities, types, categories, engines, metadata_types }
  if (response.data && 'severities' in response.data && 'categories' in response.data) {
    return response.data;
  }

  // Throw error if response format is unexpected
  throw new Error('Invalid response format from /security/filters endpoint');
}

/**
 * Get security findings with pagination and filters
 * GET /security/findings?org_id=xxx&user_id=xxx&page=1&pageSize=10&severity=High&search=CRUD
 *
 * @param params Query parameters
 * @returns Security findings data with pagination
 */
export async function getSecurityFindings(params: {
  org_id: number | string;
  user_id: number | string;
  page?: number;
  pageSize?: number;
  severity?: string;
  category?: string;
  metadata_type?: string;
  search?: string;
}): Promise<SecurityFindingsResponse> {
  const queryParams: Record<string, any> = {
    org_id: params.org_id,
    user_id: params.user_id,
    page: params.page || 1,
    pageSize: params.pageSize || 10,
  };

  // Add optional filters if provided
  if (params.severity && params.severity !== 'all') {
    queryParams.severity = params.severity;
  }
  if (params.category && params.category !== 'all') {
    queryParams.category = params.category;
  }
  if (params.metadata_type) {
    queryParams.metadata_type = params.metadata_type;
  }
  if (params.search) {
    queryParams.search = params.search;
  }

  // Define the API response type (findings as object keyed by metadata_item_id)
  interface ApiResponse {
    summary: SecurityFindingsSummary;
    pagination: SecurityFindingsPagination;
    findings: ApiFindingsObject;
  }

  const response = await ragServiceAxios.get<ApiResponse>('/security/findings', {
    params: queryParams,
    headers: {
      'X-Organization-ID': String(params.org_id),
      'X-User-ID': String(params.user_id),
    },
  });

  // Response format after interceptor: { summary, pagination, findings }
  if (response.data && 'findings' in response.data && 'pagination' in response.data) {
    const apiData = response.data;

    // Transform findings object to array of metadata items (hierarchical)
    // API returns: { findings: { "uuid-1": { metadata_item_id, file_name, findings: [...] }, ... } }
    // We return: { metadataItems: [{ metadata_item_id, file_name, findings: [...] }, ...] }
    const metadataItems: ApiMetadataItem[] = [];

    if (apiData.findings && typeof apiData.findings === 'object') {
      Object.values(apiData.findings).forEach((metadataItem) => {
        metadataItems.push(metadataItem);
      });
    }

    // Also create flat findings array for backward compatibility
    const findingsArray: SecurityFinding[] = [];
    metadataItems.forEach((metadataItem) => {
      if (metadataItem.findings && Array.isArray(metadataItem.findings)) {
        metadataItem.findings.forEach((finding) => {
          findingsArray.push({
            ...finding,
            affected_object: metadataItem.file_name,
            metadata_type: metadataItem.metadata_type,
            metadata_item_id: metadataItem.metadata_item_id,
          });
        });
      }
    });

    return {
      summary: apiData.summary,
      pagination: apiData.pagination,
      findings: findingsArray,
      metadataItems: metadataItems,
    };
  }

  // Throw error if response format is unexpected
  throw new Error('Invalid response format from /security/findings endpoint');
}

// Security Scan Types
export interface SecurityScanJobResponse {
  job_id: string;
  status: 'queued' | 'running' | 'completed' | 'failed';
  filters: Record<string, any>;
  status_url: string;
  stream_url: string;
}

export interface SecurityScanStatus {
  job_id: string;
  org_id: string | number;
  user_id: string | number;
  scan_type: string;
  status: 'queued' | 'running' | 'completed' | 'failed';
  progress: {
    total: number;
    current: number;
    percent: number;
  };
  result: any;
  error_message: string | null;
  metadata_item_ids: number[];
  filters: Record<string, any> | null;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
}

/**
 * Start a complete security scan
 * POST /security/scan/all/async
 *
 * @param params Request body
 * @returns Job information
 */
export async function startSecurityScan(params: {
  org_id: string | number;
  user_id: string | number;
}): Promise<SecurityScanJobResponse> {
  const response = await ragServiceAxios.post<SecurityScanJobResponse>(
    '/security/scan/all/async',
    {
      org_id: params.org_id,
      user_id: params.user_id,
    },
    {
      headers: {
        'X-Organization-ID': String(params.org_id),
        'X-User-ID': String(params.user_id),
      },
    }
  );

  if (response.data && 'job_id' in response.data) {
    return response.data;
  }

  throw new Error('Invalid response format from /security/scan/all/async endpoint');
}

/** Response when there is an ongoing security scan job */
export interface SecurityOngoingJobResponse {
  job_id: string;
}

/** Job item from GET /api/v2/jobs (running) */
export interface SecurityScanJobItem {
  job_id: string;
  scan_type: string;
  status: string;
  progress: { total: number; current: number; percent: number };
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
  job_type: string;
  metadata_retrieval_id: string | null;
  status_url: string;
  stream_url: string;
}

/** Response from GET /api/v2/jobs */
export interface SecurityScanJobsResponse {
  jobs: SecurityScanJobItem[];
  job_type_filter: string | null;
}

/**
 * Get the current ongoing security scan job for the given org/user (if any).
 * Used to restore progress after page refresh and to prevent starting a duplicate scan.
 * GET /api/v2/jobs?org_id=xxx&user_id=xxx&status=running
 *
 * Only jobs with job_type === 'scan' trigger streaming/rehydration.
 *
 * @param params Query parameters
 * @returns { job_id } when there is a running scan job, null otherwise
 */
export async function getOngoingSecurityScanJob(params: {
  org_id: string | number;
  user_id: string | number;
}): Promise<SecurityOngoingJobResponse | null> {
  try {
    const response = await ragServiceJobsAxios.get<SecurityScanJobsResponse>(
      '/api/v2/jobs',
      {
        params: {
          org_id: params.org_id,
          user_id: params.user_id,
          status: 'running',
        },
        headers: {
          'X-Organization-ID': String(params.org_id),
          'X-User-ID': String(params.user_id),
        },
      }
    );
    const jobs = response.data?.jobs;
    if (Array.isArray(jobs)) {
      const scanJob = jobs.find((job) => job.job_type === 'scan' && job.job_id);
      if (scanJob) {
        return { job_id: scanJob.job_id };
      }
    }
    return null;
  } catch (err: unknown) {
    const ax = err as { response?: { status?: number } };
    if (ax?.response?.status === 404) {
      return null;
    }
    throw err;
  }
}

/**
 * Subscribe to security scan status updates via SSE
 * GET /security/scan/status/{job_id}/stream?org_id=xxx&user_id=xxx
 *
 * @param params Query parameters
 * @param onMessage Callback for each SSE message
 * @param onError Callback for errors
 * @param onComplete Callback when stream completes
 * @returns Function to close the connection
 */
export function subscribeToSecurityScanStatus(
  params: {
    job_id: string;
    org_id: string | number;
    user_id: string | number;
  },
  onMessage: (status: SecurityScanStatus) => void,
  onError?: (error: Error) => void,
  onComplete?: () => void
): () => void {
  const token = getAuthToken();
  const url = `${RAG_SERVICE_BASE_URL}/security/scan/status/${params.job_id}/stream?org_id=${params.org_id}&user_id=${params.user_id}`;

  const abortController = new AbortController();

  (async () => {
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'text/event-stream',
          'X-Organization-ID': String(params.org_id),
          'X-User-ID': String(params.user_id),
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        signal: abortController.signal,
      });

      if (!response.ok) {
        const text = await response.text().catch(() => 'Unknown error');
        if (onError) onError(new Error(`SSE HTTP ${response.status}: ${text}`));
        return;
      }

      const reader = response.body?.getReader();
      if (!reader) {
        if (onError) onError(new Error('No response body reader available'));
        return;
      }

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const data = JSON.parse(line.slice(6)) as SecurityScanStatus;
            onMessage(data);
            if (data.status === 'completed' || data.status === 'failed') {
              if (onComplete) onComplete();
              return;
            }
          } catch (err) {
            console.error('Error parsing SSE message:', err);
            if (onError) onError(err as Error);
          }
        }
      }
    } catch (err: any) {
      if (err?.name === 'AbortError') return;
      console.error('SSE connection error:', err);
      if (onError) onError(err as Error);
    }
  })();

  // Return cleanup function
  return () => {
    abortController.abort();
  };
}

// ============================================================================
// Security Report PDF
// ============================================================================

/**
 * Download the latest security scan report as a PDF blob.
 * GET /security/scan/report/pdf/latest?org_id=xxx&user_id=xxx
 *
 * The API always returns HTTP 200:
 *   - PDF found  → Content-Type: application/pdf, body: binary PDF
 *   - No report  → Content-Type: application/json, body: { status: "error", message: "..." }
 */
export async function getSecurityReportPdf(params: {
  org_id: string | number;
  user_id: string | number;
}): Promise<Blob> {
  const token = getAuthToken();
  const url = `${RAG_SERVICE_BASE_URL}/security/scan/report/pdf/latest?org_id=${params.org_id}&user_id=${params.user_id}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'X-Organization-ID': String(params.org_id),
      'X-User-ID': String(params.user_id),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  const contentType = response.headers.get('Content-Type') || '';
  if (!contentType.includes('application/pdf')) {
    const body = await response.json().catch(() => ({}));
    throw new Error((body as { message?: string }).message || 'No scan report available');
  }

  return response.blob();
}

// ============================================================================
// Ingest Job Streaming
// ============================================================================

/** connection event payload from GET /api/v2/stream/job/{job_id} */
export interface IngestConnectionEvent {
  status: string;
  connection_id: string;
  job_id: string;
  org_id: string;
  retrieval_id: string | null;
  timestamp: string;
}

/** status_update event payload from GET /api/v2/stream/job/{job_id} */
export interface IngestStatusUpdateEvent {
  job_id: string;
  org_id: string;
  status: 'queued' | 'running' | 'completed' | 'failed';
  progress: number; // 0-100
  stage: string | null;
  message: string;
  timestamp: string;
  is_snapshot?: boolean;
}

/** stage_started / stage_completed / job_completed event payload */
export interface IngestStageEvent {
  job_id: string;
  org_id: string;
  metadata_retrieval_id: string;
  stage: string;
  status: 'started' | 'completed';
  progress: number; // 0-100
  message: string;
  timestamp: string;
  duration_seconds?: number;
  version_id?: number;
}

/** heartbeat event payload */
export interface IngestHeartbeatEvent {
  job_id: string;
  timestamp: string;
}

export type IngestJobEventType =
  | 'connection'
  | 'status_update'
  | 'stage_started'
  | 'stage_completed'
  | 'heartbeat'
  | 'job_completed';

export interface IngestJobCallbacks {
  onConnection?: (event: IngestConnectionEvent) => void;
  onStatusUpdate?: (event: IngestStatusUpdateEvent) => void;
  onStageStarted?: (event: IngestStageEvent) => void;
  onStageCompleted?: (event: IngestStageEvent) => void;
  onHeartbeat?: (event: IngestHeartbeatEvent) => void;
  onJobCompleted?: (event: IngestStageEvent) => void;
  onError?: (error: Error) => void;
  onComplete?: () => void;
  /** Fired when the stream closes without a terminal event (backend drop or 45s inactivity). Caller should reconnect. */
  onDisconnected?: () => void;
}

/**
 * Get the current ongoing ingest job for the given org/user (if any).
 * Filters /api/v2/jobs results for job_ty  pe === 'ingest'.
 */
export async function getOngoingIngestJob(params: {
  org_id: string | number;
  user_id: string | number;
}): Promise<SecurityOngoingJobResponse | null> {
  try {
    const response = await ragServiceJobsAxios.get<SecurityScanJobsResponse>(
      '/api/v2/jobs',
      {
        params: {
          org_id: params.org_id,
          user_id: params.user_id,
          status: 'running',
        },
        headers: {
          'X-Organization-ID': String(params.org_id),
          'X-User-ID': String(params.user_id),
        },
      }
    );
    const jobs = response.data?.jobs;
    if (Array.isArray(jobs)) {
      const ingestJob = jobs.find((job) => job.job_type === 'ingest' && job.job_id);
      if (ingestJob) {
        return { job_id: ingestJob.job_id };
      }
    }
    return null;
  } catch (err: unknown) {
    const ax = err as { response?: { status?: number } };
    if (ax?.response?.status === 404) {
      return null;
    }
    throw err;
  }
}

const INGEST_WATCHDOG_TIMEOUT_MS = 30_000;

/**
 * Subscribe to ingest job stream via SSE.
 * GET /api/v2/stream/job/{job_id}?org_id=xxx&user_id=xxx
 *
 * Resilience:
 * - Watchdog: if no event arrives within 45s, aborts and fires onDisconnected.
 * - Premature close: if the stream closes without a terminal event, fires onDisconnected.
 * Caller (ingestJobStore) reconnects on onDisconnected up to MAX_INGEST_RECONNECTS times.
 *
 * @returns Cleanup function that aborts the connection
 */
export function subscribeToIngestJobStream(
  params: {
    job_id: string;
    org_id: string | number;
    user_id: string | number;
  },
  callbacks: IngestJobCallbacks
): () => void {
  const token = getAuthToken();
  const url = `${RAG_SERVICE_JOBS_BASE_URL}/api/v2/stream/job/${params.job_id}?org_id=${params.org_id}&user_id=${params.user_id}`;
  const abortController = new AbortController();

  (async () => {
    let isTerminated = false;
    let watchdogTimer: ReturnType<typeof setTimeout> | null = null;

    const clearWatchdog = () => {
      if (watchdogTimer) { clearTimeout(watchdogTimer); watchdogTimer = null; }
    };

    const armWatchdog = () => {
      clearWatchdog();
      watchdogTimer = setTimeout(() => {
        if (!isTerminated) {
          abortController.abort();
          callbacks.onDisconnected?.();
        }
      }, INGEST_WATCHDOG_TIMEOUT_MS);
    };

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Accept: 'text/event-stream',
          'X-Organization-ID': String(params.org_id),
          'X-User-ID': String(params.user_id),
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        signal: abortController.signal,
      });

      if (!response.ok) {
        const text = await response.text().catch(() => 'Unknown error');
        if (callbacks.onError) callbacks.onError(new Error(`SSE HTTP ${response.status}: ${text}`));
        return;
      }

      const reader = response.body?.getReader();
      if (!reader) {
        if (callbacks.onError) callbacks.onError(new Error('No response body reader available'));
        return;
      }

      armWatchdog(); // Start watchdog after successfully opening the stream

      const decoder = new TextDecoder();
      let buffer = '';
      let currentEventType: IngestJobEventType | null = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('event: ')) {
            currentEventType = line.slice(7).trim() as IngestJobEventType;
          } else if (line.startsWith('data: ')) {
            armWatchdog(); // Reset inactivity timer on every data event
            try {
              const payload = JSON.parse(line.slice(6));

              if (currentEventType === 'connection') {
                callbacks.onConnection?.(payload as IngestConnectionEvent);
              } else if (currentEventType === 'status_update') {
                const event = payload as IngestStatusUpdateEvent;
                callbacks.onStatusUpdate?.(event);
                if (event.status === 'completed' || event.status === 'failed') {
                  isTerminated = true;
                  clearWatchdog();
                  callbacks.onComplete?.();
                  return;
                }
              } else if (currentEventType === 'stage_started') {
                callbacks.onStageStarted?.(payload as IngestStageEvent);
              } else if (currentEventType === 'stage_completed') {
                callbacks.onStageCompleted?.(payload as IngestStageEvent);
              } else if (currentEventType === 'heartbeat') {
                callbacks.onHeartbeat?.(payload as IngestHeartbeatEvent);
              } else if (currentEventType === 'job_completed') {
                isTerminated = true;
                clearWatchdog();
                callbacks.onJobCompleted?.(payload as IngestStageEvent);
                callbacks.onComplete?.();
                return;
              }

              // Reset event type after consuming data
              currentEventType = null;
            } catch (err) {
              console.error('Error parsing ingest SSE message:', err);
              callbacks.onError?.(err as Error);
            }
          }
          // Skip id: lines — no action needed
        }
      }

      // Stream ended (done: true) without a terminal event — backend dropped the connection
      clearWatchdog();
      if (!isTerminated) {
        callbacks.onDisconnected?.();
      }
    } catch (err: any) {
      clearWatchdog();
      if (err?.name === 'AbortError') return;
      console.error('Ingest SSE connection error:', err);
      callbacks.onError?.(err as Error);
    }
  })();

  return () => {
    abortController.abort();
  };
}
